/** Task collaboration: assignment, comments, mentions, watchers, notifications. */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';

import { api, queryKeys } from '@/shared/api';

import type { AppNotification, CardComment, Mention, NotificationPage, WatchState } from './types';

// --- comments -----------------------------------------------------------------------------------

export function useComments(cardId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.collab.comments(cardId),
    queryFn: ({ signal }) => api.get<CardComment[]>(`/v1/cards/${cardId}/comments`, { signal }),
    enabled,
  });
}

export function useMentionable(cardId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.collab.mentionable(cardId),
    queryFn: ({ signal }) => api.get<Mention[]>(`/v1/cards/${cardId}/mentionable`, { signal }),
    enabled,
  });
}

export function useCreateComment(cardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      body: string;
      mention_user_ids: string[];
      /** Answer this comment (one level: a reply to a reply stays in the same thread). */
      reply_to_comment_id?: string;
    }) => api.post<CardComment>(`/v1/cards/${cardId}/comments`, data),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.collab.comments(cardId) });
    },
  });
}

export function useUpdateComment(cardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      commentId,
      ...data
    }: {
      commentId: string;
      body: string;
      mention_user_ids: string[];
    }) => api.patch<CardComment>(`/v1/cards/${cardId}/comments/${commentId}`, data),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.collab.comments(cardId) });
    },
  });
}

export function useDeleteComment(cardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => api.delete(`/v1/cards/${cardId}/comments/${commentId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.collab.comments(cardId) });
    },
  });
}

// --- watchers -----------------------------------------------------------------------------------

export function useWatchState(cardId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.collab.watchers(cardId),
    queryFn: ({ signal }) => api.get<WatchState>(`/v1/cards/${cardId}/watchers`, { signal }),
    enabled,
  });
}

export function useSetWatching(cardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (watching: boolean) =>
      watching
        ? api.put<WatchState>(`/v1/cards/${cardId}/watchers/me`)
        : api.delete<WatchState>(`/v1/cards/${cardId}/watchers/me`),
    onSuccess: (state) => {
      client.setQueryData(queryKeys.collab.watchers(cardId), state);
    },
  });
}

// --- notifications ------------------------------------------------------------------------------

type Pages = InfiniteData<NotificationPage, string | null>;

/**
 * The bell's inbox lists UNREAD notifications only: reading one makes it disappear. The history
 * page uses the same endpoint without the filter (all, newest first, with a read / unread filter).
 */
export function useNotifications(
  options: { unreadOnly?: boolean; readState?: 'all' | 'unread' | 'read'; enabled?: boolean } = {},
) {
  const { unreadOnly = false, readState = 'all', enabled = true } = options;
  return useInfiniteQuery<NotificationPage, Error, Pages, readonly string[], string | null>({
    queryKey: unreadOnly ? [...queryKeys.collab.inbox] : [...queryKeys.collab.history(readState)],
    initialPageParam: null,
    queryFn: ({ signal, pageParam }) =>
      api.get<NotificationPage>('/v1/notifications', {
        signal,
        query: {
          before: pageParam,
          limit: unreadOnly ? 15 : 30,
          unread_only: unreadOnly || readState === 'unread' ? true : undefined,
        },
      }),
    getNextPageParam: (last) => last.next_cursor,
    enabled,
  });
}

export function useUnreadCount(enabled = true) {
  return useQuery({
    queryKey: queryKeys.collab.unread,
    queryFn: ({ signal }) =>
      api.get<{ unread_count: number }>('/v1/notifications/unread-count', { signal }),
    select: (data) => data.unread_count,
    enabled,
  });
}

export function useMarkNotificationRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) =>
      api.put<{ unread_count: number }>(`/v1/notifications/${notificationId}/read`),
    onSuccess: (result, notificationId) => {
      client.setQueryData(queryKeys.collab.unread, result);
      // The inbox shows unread items only: a read one leaves it at once.
      client.setQueryData<Pages>(queryKeys.collab.inbox, (pages) =>
        pages
          ? {
              ...pages,
              pages: pages.pages.map((page) => ({
                ...page,
                unread_count: result.unread_count,
                items: page.items.filter((n) => n.id !== notificationId),
              })),
            }
          : pages,
      );
      // History keeps it, marked as read.
      client.setQueriesData<Pages>({ queryKey: ['collab', 'notifications', 'history'] }, (pages) =>
        pages
          ? {
              ...pages,
              pages: pages.pages.map((page) => ({
                ...page,
                unread_count: result.unread_count,
                items: page.items.map((n) =>
                  n.id === notificationId && n.read_at === null
                    ? { ...n, read_at: new Date().toISOString() }
                    : n,
                ),
              })),
            }
          : pages,
      );
    },
  });
}

export function useMarkAllRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ unread_count: number }>('/v1/notifications/read-all'),
    onSuccess: () => {
      client.setQueryData(queryKeys.collab.unread, { unread_count: 0 });
      void client.invalidateQueries({ queryKey: queryKeys.collab.notifications });
    },
  });
}

export function flattenNotifications(data: Pages | undefined): AppNotification[] {
  return data?.pages.flatMap((page) => page.items) ?? [];
}

// --- assignment ---------------------------------------------------------------------------------

export function useSetAssignment(boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      cardId,
      kind,
      assigneeIds,
      expectedVersion,
    }: {
      cardId: string;
      kind: 'unassigned' | 'assigned' | 'shared';
      assigneeIds: string[];
      expectedVersion?: number;
    }) =>
      api.put(`/v1/cards/${cardId}/assignment`, {
        kind,
        assignee_ids: assigneeIds,
        ...(expectedVersion !== undefined && { expected_version: expectedVersion }),
      }),
    onSuccess: (_card, { cardId }) => {
      void client.invalidateQueries({ queryKey: queryKeys.boards.detail(boardId) });
      void client.invalidateQueries({ queryKey: queryKeys.cards.detail(cardId) });
      void client.invalidateQueries({ queryKey: queryKeys.cards.history(cardId) });
      void client.invalidateQueries({ queryKey: queryKeys.collab.watchers(cardId) });
      void client.invalidateQueries({ queryKey: queryKeys.calendar.all });
    },
  });
}
